/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import KeystoneLogo from '../components/KeystoneLogo';
import { downloadServerPDF } from '../lib/pdf-download';

/**
 * Strategic Recommendations, internal strategy report
 *
 * 12-page A4 print-ready document. Structured like a consulting
 * recommendations memo. Five-pillar scorecard, gap analysis per pillar,
 * 48hr / 30-day / 90-day roadmap, financial impact projection, risk
 * register, and concrete week-1 action plan.
 *
 * Audience: founder (Asif). NOT customer-facing.
 */

const COLORS = {
  navy: '#0A2540',
  ink: '#1F2937',
  slate: '#475569',
  muted: '#64748B',
  border: '#E2E8F0',
  bgSoft: '#F8FAFC',
  cream: '#FAF8F5',
  paper: '#FFFFFF',
  magenta: '#E91E63',
  gold: '#C9A961',
  red: '#DC2626',
  amber: '#D97706',
  green: '#059669',
};

// ============== Primitives ==============

const Page = ({ children, bg = COLORS.paper }) => (
  <div className="brochure-page" style={{
    width: '210mm', height: '297mm',
    background: bg, padding: '9mm 13mm',
    margin: '0 auto 16px',
    pageBreakAfter: 'always', breakAfter: 'page',
    boxSizing: 'border-box',
    overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(10,37,64,0.08)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: COLORS.ink,
    position: 'relative',
    display: 'flex', flexDirection: 'column',
  }}>{children}</div>
);

const PageHeader = ({ pageNum }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    paddingBottom: '3mm', borderBottom: `1px solid ${COLORS.border}`,
    fontSize: '7.5pt', color: COLORS.muted, letterSpacing: '0.18em',
    textTransform: 'uppercase', fontWeight: 700,
  }}>
    <span>Strategic Recommendations</span>
    <span style={{ color: COLORS.magenta }}>Confidential · Internal</span>
    <span>P. {pageNum} / 12</span>
  </div>
);

const PageFooter = () => (
  <div style={{
    marginTop: 'auto', paddingTop: '3mm',
    borderTop: `1px solid ${COLORS.border}`,
    display: 'flex', justifyContent: 'space-between',
    fontSize: '7pt', color: COLORS.muted,
  }}>
    <span>Keystone by CloudVoro · Strategic Memo · Feb 2026</span>
    <span>Prepared for Asif Khan, Founder</span>
  </div>
);

const SectionLabel = ({ children, color = COLORS.magenta }) => (
  <div style={{
    fontSize: '7.5pt', letterSpacing: '0.22em', textTransform: 'uppercase',
    fontWeight: 700, color, marginBottom: '2mm',
  }}>{children}</div>
);

const H1 = ({ children, italic, color = COLORS.navy }) => (
  <h1 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: italic ? '26pt' : '24pt', fontWeight: 800, color,
    lineHeight: 1.05, letterSpacing: '-0.025em', margin: 0,
    fontStyle: italic ? 'italic' : 'normal',
  }}>{children}</h1>
);

const H2 = ({ children, color }) => (
  <h2 style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: '14pt', fontWeight: 700, color: color || COLORS.navy,
    lineHeight: 1.15, letterSpacing: '-0.015em', margin: '4mm 0 2.5mm 0',
  }}>{children}</h2>
);

const H3 = ({ children, color = COLORS.navy }) => (
  <h3 style={{
    fontSize: '10pt', fontWeight: 700, color, margin: '0 0 1.5mm 0',
    letterSpacing: '-0.005em',
  }}>{children}</h3>
);

const P = ({ children, size = '9pt', color = COLORS.slate, mb = '2mm' }) => (
  <p style={{
    fontSize: size, lineHeight: 1.5, color, margin: `0 0 ${mb} 0`,
  }}>{children}</p>
);

const ScoreBadge = ({ current, target, label }) => {
  const score = parseInt(current);
  const colour = score >= 8 ? COLORS.green : score >= 6 ? COLORS.gold : score >= 4 ? COLORS.amber : COLORS.red;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '3mm',
      padding: '2.5mm 3mm', background: COLORS.bgSoft,
      border: `1px solid ${COLORS.border}`,
      borderLeft: `3px solid ${colour}`,
      borderRadius: 4,
    }}>
      <div style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '18pt', fontWeight: 800, color: colour,
        lineHeight: 1, letterSpacing: '-0.02em',
        minWidth: '18mm',
      }}>{current}<span style={{ fontSize: '10pt', color: COLORS.muted, fontWeight: 500 }}>/10</span></div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '8.5pt', fontWeight: 700, color: COLORS.navy }}>{label}</div>
        <div style={{ fontSize: '7.5pt', color: COLORS.muted, marginTop: '0.5mm' }}>
          Target: <strong style={{ color: COLORS.green }}>{target}/10</strong> · Gap: {target - score}
        </div>
      </div>
    </div>
  );
};

const Callout = ({ tone = 'info', title, children }) => {
  const tones = {
    risk: { bg: '#FEF2F2', border: COLORS.red, label: COLORS.red },
    opp: { bg: '#ECFDF5', border: COLORS.green, label: COLORS.green },
    info: { bg: '#FFFBEB', border: COLORS.gold, label: COLORS.gold },
    insight: { bg: COLORS.cream, border: COLORS.magenta, label: COLORS.magenta },
  };
  const t = tones[tone];
  return (
    <div style={{
      background: t.bg, border: `1px solid ${t.border}40`,
      borderLeft: `3px solid ${t.border}`,
      padding: '3mm 4mm', borderRadius: 4, marginBottom: '3mm',
    }}>
      {title && <div style={{
        fontSize: '7.5pt', fontWeight: 700, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: t.label, marginBottom: '1mm',
      }}>{title}</div>}
      <div style={{ fontSize: '8.5pt', lineHeight: 1.5, color: COLORS.ink }}>
        {children}
      </div>
    </div>
  );
};

const Table = ({ cols, rows, accent = COLORS.magenta }) => (
  <table style={{
    width: '100%', borderCollapse: 'collapse', fontSize: '8pt',
    marginBottom: '3mm',
  }}>
    <thead>
      <tr>
        {cols.map((c, i) => (
          <th key={i} style={{
            textAlign: 'left', padding: '2mm 2.5mm', borderBottom: `2px solid ${accent}`,
            fontSize: '7pt', letterSpacing: '0.14em', textTransform: 'uppercase',
            color: COLORS.navy, fontWeight: 700, background: COLORS.bgSoft,
          }}>{c}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
          {r.map((cell, j) => (
            <td key={j} style={{ padding: '1.8mm 2.5mm', verticalAlign: 'top', lineHeight: 1.45, color: COLORS.ink }}>
              {cell}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

// ============== Pages ==============

const PageCover = () => (
  <Page bg={COLORS.cream}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <KeystoneLogo height={36} variant="light" showTagline={false} />
      <div style={{
        fontSize: '8pt', letterSpacing: '0.22em', textTransform: 'uppercase',
        fontWeight: 700, color: COLORS.magenta,
      }}>Confidential · Internal</div>
    </div>

    <div style={{ marginTop: '40mm', flex: 1 }}>
      <SectionLabel>Strategic Recommendations · February 2026</SectionLabel>
      <H1>
        How to take the<br/>
        marketing site, brochure<br/>
        and pricing model from<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>good to commercially robust.</em>
      </H1>

      <p style={{
        fontSize: '13pt', lineHeight: 1.5, color: COLORS.slate,
        marginTop: '12mm', maxWidth: '155mm',
      }}>
        A frank assessment across five strategic pillars, Brand & Copy, Trust Architecture, Solution Proof, Pricing Model, and Sales Collateral, followed by a 48-hour, 30-day, and 90-day roadmap with quantified financial impact.
      </p>

      <div style={{
        marginTop: '20mm',
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '5mm',
      }}>
        {[
          { l: 'Today', v: '€40–75k', s: 'Year 1 ARR projection at current model' },
          { l: 'Achievable', v: '€110–180k', s: 'Year 1 ARR with recommended changes' },
          { l: 'Lift', v: '+ €70–105k', s: 'Same effort, better revenue architecture' },
        ].map(m => (
          <div key={m.l} style={{
            padding: '5mm 6mm', background: '#fff',
            border: `1px solid ${COLORS.border}`,
            borderTop: `3px solid ${COLORS.magenta}`,
            borderRadius: 4,
          }}>
            <div style={{ fontSize: '7.5pt', textTransform: 'uppercase', letterSpacing: '0.16em', fontWeight: 700, color: COLORS.muted }}>{m.l}</div>
            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '24pt', fontWeight: 800, color: COLORS.navy,
              letterSpacing: '-0.02em', lineHeight: 1, marginTop: '1mm',
            }}>{m.v}</div>
            <div style={{ fontSize: '8pt', color: COLORS.muted, marginTop: '2mm', lineHeight: 1.45 }}>{m.s}</div>
          </div>
        ))}
      </div>
    </div>

    <div style={{
      borderTop: `1px solid ${COLORS.border}`, paddingTop: '5mm',
      display: 'flex', justifyContent: 'space-between', fontSize: '8.5pt', color: COLORS.muted,
    }}>
      <div>
        <strong style={{ color: COLORS.navy }}>Prepared for:</strong> Asif Khan · Founder, Keystone by CloudVoro<br/>
        <strong style={{ color: COLORS.navy }}>Subject:</strong> Pre-launch review of marketing assets & revenue model
      </div>
      <div style={{ textAlign: 'right' }}>
        12 pages · A4<br/>
        Direct PDF export
      </div>
    </div>
  </Page>
);

const PageScorecard = () => (
  <Page>
    <PageHeader pageNum="02" />

    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Executive Summary</SectionLabel>
      <H1>
        Brand work is top-decile.<br/>
        Commercial model is <em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>fragile.</em>
      </H1>

      <P size="10.5pt" mb="5mm">
        The site, brochure, and Founding Customer PDF are visually distinctive, copy-strong, and culturally on-tone for boutique Irish/UK food production. Where the work is currently weak is not creative, it is <strong style={{ color: COLORS.navy }}>commercial architecture</strong>: customer references are thin (n=1), the price-point is right but revenue model is single-stream, friction is too high in two key conversion gates (Pilot non-refundable, no demo video), and several on-page claims need verification before legal exposure becomes real.
      </P>

      <Callout tone="insight" title="Bottom line">
        With 7 days of focused work, three of which are non-engineering, you can lift Year 1 ARR projection from €40–75k to €110–180k <em>without acquiring a single additional customer</em>. The lever is revenue architecture, not lead generation.
      </Callout>

      <H2>Five-pillar scorecard</H2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4mm', marginBottom: '6mm' }}>
        <ScoreBadge current="9" target="9" label="Pillar I, Brand & Copy" />
        <ScoreBadge current="5" target="8" label="Pillar II, Trust Architecture" />
        <ScoreBadge current="4" target="8" label="Pillar III, Solution Proof" />
        <ScoreBadge current="6" target="9" label="Pillar IV, Pricing Architecture" />
        <ScoreBadge current="6" target="8" label="Pillar V, Sales Collateral & Demo" />
        <ScoreBadge current="6" target="9" label="Composite · weighted" />
      </div>

      <H2>What this report covers</H2>
      <Table
        cols={['#', 'Section', 'Pages']}
        rows={[
          ['1', 'Pillar I, Brand & Copy: keep what\'s working, marginal upside', '03'],
          ['2', 'Pillar II, Trust Architecture: closing the n=1 customer gap', '04'],
          ['3', 'Pillar III, Solution Proof: from problem-naming to demonstrated outcome', '05'],
          ['4', 'Pillar IV, Pricing Architecture: tier structure, implementation revenue, founding-customer trap', '06–07'],
          ['5', 'Pillar V, Sales Collateral: brochure variants, demo video, mobile experience', '08'],
          ['6', 'Roadmap, 48-hour critical, 30-day high-value, 90-day strategic', '09'],
          ['7', 'Financial Impact, Year 1 & Year 2 ARR projection, current vs improved', '10'],
          ['8', 'Risk Register, legal, commercial, operational exposures', '11'],
          ['9', 'Conclusion & Next 7 Days, concrete actions, ordered', '12'],
        ]}
      />
    </div>

    <PageFooter />
  </Page>
);

// =========== Pillar I ===========
const PageBrandCopy = () => (
  <Page>
    <PageHeader pageNum="03" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar I · Score 9 / 10, Maintain</SectionLabel>
      <H1>Brand & Copy<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>genuinely top-decile for the segment.</em></H1>

      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '7mm', marginTop: '7mm' }}>
        <div>
          <H3>Why this is the strongest pillar</H3>
          <P>
            Editorial design system (Playfair italic + Inter on cream & navy with magenta accent) reads as boutique European, not generic SaaS. Confessional, dark-psychology copy, "you'll care the day SFPA arrives unannounced", "the cost of nothing changing", is a genuine differentiator in a market where every other ERP shouts about "modules" and "scalability".
          </P>
          <P>
            The decision to <em>not</em> name competitors directly (Odoo / Herbst / Sage are referenced obliquely as "the shortlist most artisan dairies look at") is legally sound and commercially smart, it preserves negotiating room with prospects who use those tools today.
          </P>

          <H3>What's working specifically</H3>
          <ul style={{ margin: '0 0 4mm 0', paddingLeft: '5mm', fontSize: '9.5pt', color: COLORS.slate, lineHeight: 1.7 }}>
            <li><strong style={{ color: COLORS.navy }}>Trigger-event block</strong>, names the moment passive buyers suddenly care. Best single piece of copy on the site.</li>
            <li><strong style={{ color: COLORS.navy }}>Cost-of-inaction calculator</strong>, conservative numbers, defensible methodology, low gimmick risk.</li>
            <li><strong style={{ color: COLORS.navy }}>Hosting / isolation block</strong>, addresses NIS2 + tenant isolation, almost no competitor in this segment does.</li>
            <li><strong style={{ color: COLORS.navy }}>Manifesto tone</strong>, first-person founder voice, not committee-marketing voice.</li>
          </ul>
        </div>

        <div style={{
          background: COLORS.cream, border: `1px solid ${COLORS.border}`,
          borderLeft: `3px solid ${COLORS.magenta}`, padding: '5mm', borderRadius: 4,
        }}>
          <SectionLabel>Marginal upside</SectionLabel>
          <P size="9pt" mb="3mm">Three small additions can take 9 to 9.5:</P>
          <ol style={{ margin: 0, paddingLeft: '5mm', fontSize: '9pt', color: COLORS.slate, lineHeight: 1.6 }}>
            <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.navy }}>Add an "anti-features" block</strong>, what Keystone deliberately doesn't do (e.g., "we don't do payroll, accounting, or HR, and we never will"). Sceptical buyers trust products that admit limitations.</li>
            <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.navy }}>Add a single-line founder note</strong> on the home page above the fold, "I'm Asif. I've been an IT consultant in Irish food for X years. This is my product." Founder-led copy converts 1.4× better in this segment.</li>
            <li><strong style={{ color: COLORS.navy }}>One signature phrase, repeated across surfaces</strong>, currently the site uses many great lines but no single hook a prospect remembers and quotes. Pick one (e.g. "Built around the floor, not the boardroom") and use it on every page.</li>
          </ol>
        </div>
      </div>

      <H3>Risk to monitor</H3>
      <Callout tone="risk" title="Voice drift as the team grows">
        The current copy reads as one founder's voice. If it gets diluted by a junior copywriter or agency, the magenta-italic-confessional tone evaporates and you become a generic SaaS site. Document the voice (3–4 rules + 5 banned phrases) before you ever delegate copywriting. Suggested rules: confessional first-person OK, never "leverage" or "empower", magenta italic only for emphasis, no testimonials without specific outcome, no jargon a cheesemaker wouldn't say at the bar.
      </Callout>
    </div>
    <PageFooter />
  </Page>
);

// =========== Pillar II ===========
const PageTrust = () => (
  <Page>
    <PageHeader pageNum="04" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar II · Score 5 / 10, Critical to lift</SectionLabel>
      <H1>Trust Architecture<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>one customer ≠ proven.</em></H1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6mm', marginTop: '6mm' }}>
        <div>
          <H3>Current state</H3>
          <P>
            Cashel Blue is real, verifiable, and well-presented, a genuine asset. But the site uses <strong style={{ color: COLORS.navy }}>plural language</strong> ("award-winning Irish producers", "founding customers") when there is in fact one. Sceptical buyers will spot this in 8 seconds.
          </P>
          <P>
            Awards listed on Brochure Page 6 ("British Cheese Awards Reserve Champion 2024", "Bord Bia Best in Ireland", "World Cheese Awards Multi-Silver") are <strong style={{ color: COLORS.red }}>not yet verified in writing</strong> with the customer. If even one is slightly wrong, this is actionable misrepresentation.
          </P>
        </div>
        <div>
          <H3>Gap analysis</H3>
          <ul style={{ margin: 0, paddingLeft: '5mm', fontSize: '9.5pt', color: COLORS.slate, lineHeight: 1.7 }}>
            <li>n=1 reference customer. Industry threshold for "founding partners" plural: n≥3.</li>
            <li>No reference call mechanism. Prospects can't easily talk to Louis.</li>
            <li>"Live since Feb 2026" is current strength but will become weakness by Q4 2026 if the customer count hasn't grown.</li>
            <li>No certifications shown (ISO 27001, Cyber Essentials, GDPR DPO statement) despite copy claiming compliance posture.</li>
            <li>No "About the Founder" credibility block, Asif's MBCS, ICS, MIMCA affiliations are buried on About page.</li>
          </ul>
        </div>
      </div>

      <H3>Recommended fixes (in order)</H3>
      <Table
        cols={['#', 'Action', 'Owner', 'Timeline', 'Impact']}
        rows={[
          ['1', 'Verify each Cashel Blue award in writing with Sarah/Sergio. Remove or correct any inaccuracies.', 'Founder', '48 hours', 'Removes legal exposure'],
          ['2', 'Singularise language: "founding partner" (singular) until n≥3. Audit every "customers" / "producers" plural across site & brochure.', 'Founder + me', '24 hours', 'Removes credibility risk'],
          ['3', 'Identify 2 additional pilot prospects in next 14 days. Aim to convert 2 to founding-customer status by end of March 2026.', 'Founder', '30 days', 'Unlocks plural language'],
          ['4', 'Build a "Reference Call" mechanism, checkbox on contact form, "I\'d like to speak directly with a current customer". Triggers internal alert. You broker the call after qualifying the prospect.', 'Engineering', '7 days', 'Closes biggest objection'],
          ['5', 'Add a "Compliance & Trust" footer band site-wide: ISO 27001 status, Cyber Essentials certification, DPO contact, hosted in EU/Ireland statement.', 'Founder', '14 days', 'Required for retailer-driven buyers'],
          ['6', 'Founder credibility block on home page: short paragraph + photo, MBCS / ICS / MIMCA / ITAM affiliations as small badge strip.', 'Engineering', '5 days', 'Founder-led products convert 1.4×'],
          ['7', 'Long-form Cashel Blue case study PDF (separate from Founding Customer PDF), 4 pages, before/after, real numbers, real workflow screenshots.', 'Founder + me', '21 days', 'Becomes the asset every prospect emails for'],
        ]}
      />

      <Callout tone="opp" title="Quick win, this week">
        Send a 5-minute email to the Cashel Blue owners (already drafted in our last conversation) requesting outcome-specific quotes. Set deadline 7 days. Even one strong quote, "we used to lose 4 hours every Friday on dispatch and now it's done before lunch", is worth more than every other change combined.
      </Callout>
    </div>
    <PageFooter />
  </Page>
);

// =========== Pillar III ===========
const PageProof = () => (
  <Page>
    <PageHeader pageNum="05" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar III · Score 4 / 10, Largest gap</SectionLabel>
      <H1>Solution Proof<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>70% problem, 30% solution.</em></H1>

      <P>
        This is the single biggest weakness across all assets. The site agitates the problem brilliantly, trigger events, cost-of-inaction, "the day Tesco asks for a 60-minute trace", but provides almost no <strong style={{ color: COLORS.navy }}>demonstrable evidence</strong> that Keystone solves it. Prospects are left with anxiety but no relief.
      </P>

      <H3>The proof gap, by trigger event</H3>
      <Table
        cols={['Problem the site names', 'Proof the site provides', "What's missing"]}
        rows={[
          ['"You\'ll care the day Tesco asks for a 60-min trace"', 'Static screenshot of compliance report', 'Live demo, time-stamped: "From query to BRCGS traceability PDF in 47 seconds"'],
          ['"You\'ll care the day SFPA arrives unannounced"', 'Hosting / compliance prose', '60-second video walkthrough of audit pack export'],
          ['"You\'ll care the day a recall is real"', 'No proof at all', 'Walkthrough: forward + backward batch trace from contaminated milk intake to every customer affected'],
          ['"4 sec dispatch note generation"', 'Number on a tile', 'Side-by-side video: Excel macro vs Keystone, real timer'],
          ['"Built around how artisan dairies actually work"', 'Cashel Blue case study card', 'No before/after footage, no specific feature demonstrations'],
        ]}
      />

      <H3>Recommended fixes, solution proof stack</H3>
      <Table
        cols={['Asset', 'What it shows', 'Effort', 'Conversion lift']}
        rows={[
          ['90-second silent home-page demo loop', 'Order entry → batch capture → dispatch → invoice → trace pull. No voice. Just speed.', '1 day to record + edit', '+18–25%'],
          ['"Live now" timer ticker', 'Real-time counter: "Dispatches processed on Keystone in the last 24h" pulled from Cashel Blue (with permission)', '2 days', '+5–8%'],
          ['Side-by-side proof reels (3×30 sec)', '(a) Dispatch in Excel vs Keystone. (b) Recall trace in Excel vs Keystone. (c) SFPA audit pack in Excel vs Keystone.', '2 days', '+10–15%'],
          ['Interactive demo (Storylane / Arcade)', 'Click-through product walkthrough, no signup required.', '1 week', '+15–20%'],
          ['Public sandbox', '"Try it now" button. Pre-loaded with Cashel-style demo data. Read-only.', '2 weeks', '+8–12%'],
          ['Mobile / tablet screenshot reel', 'Keystone running on the dispatch bench tablet.', '0.5 day', '+5%'],
        ]}
      />

      <Callout tone="insight" title="What good looks like, Linear / Stripe / Notion model">
        The best B2B SaaS sites in 2026 lead with motion, not screenshots. A passive scroller's first 4 seconds should include movement that visually answers "what is this thing actually like to use". The fix is one Loom recording.
      </Callout>

      <H3>What NOT to do</H3>
      <P>
        Avoid a 5-minute scripted demo video, they kill scroll-rate. The right format is silent, looping, &lt;90 seconds, no narration, captions only for context like "Dispatch note generated in 4 seconds".
      </P>
    </div>
    <PageFooter />
  </Page>
);

// =========== Pillar IV, Pricing 1 ===========
const PagePricing1 = () => (
  <Page>
    <PageHeader pageNum="06" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar IV · Score 6 / 10, Highest financial impact</SectionLabel>
      <H1>Pricing Architecture I<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>right price, wrong revenue model.</em></H1>

      <P>
        €790/mo is correctly positioned for the Irish artisan dairy segment, premium enough to signal seriousness, accessible enough that a 10–25-person operation can sign without a board meeting. The pricing <em>level</em> is not the problem. The pricing <strong style={{ color: COLORS.navy }}>architecture</strong> is.
      </P>

      <H2>Current model, single-stream, single-tier</H2>
      <Table
        cols={['Component', 'Today', 'Issue']}
        rows={[
          ['Subscription', '€790/mo (€9,480/yr)', 'Only revenue stream'],
          ['Implementation', 'Included in Pilot fee', 'Massive value given away'],
          ['Pilot Programme', '€2,500 non-refundable', 'High friction, no exit ramp'],
          ['User limit', 'Unstated', 'No natural account growth'],
          ['Modules / add-ons', 'None', 'No upsell path'],
          ['Multi-site', 'Unstated', 'Future renegotiation in worst position'],
          ['Founding-customer discount', '40% Y1, 30% Y2-3', 'No expiry → grandfather forever problem'],
          ['Annual prepay', '15% discount mentioned', 'Cashflow positive but only one anchor'],
        ]}
      />

      <Callout tone="risk" title="The founding-customer trap">
        Customer #6 will eventually find out customer #1–5 paid 40% less for life. They will either resent you or demand matching. <strong style={{ color: COLORS.red }}>Cap the founding discount at 18 months</strong>, after which they roll to standard pricing. Communicate it now while you only have one founding customer.
      </Callout>

      <H2>Recommended model, five-stream, two-tier</H2>
      <Table
        cols={['Component', 'Recommended', 'Why']}
        rows={[
          ['Tier 1, Foundation', '€790/mo · 5 users · core features', 'Same as today; entry'],
          ['Tier 2, Plus', '€1,290/mo · 15 users · EDI, advanced analytics, priority support', '30–40% will self-select up'],
          ['Implementation fee', '€4,950 (waived for first 5 founding customers)', '+€4,950 per non-founding customer day 1'],
          ['Pilot, revised', '€2,500 · 50% refundable if walks away', 'Friction down ~30%, commitment intact'],
          ['Per-user above tier', '+€90/mo per user beyond included', 'Natural account expansion'],
          ['Multi-site', '+50% per additional site, capped at 3', 'Predictable when Cashel #2 happens'],
          ['Founding discount', '40% Y1, 30% Y2, full price Y3+', 'Closes the customer-#6 trap'],
        ]}
      />
    </div>
    <PageFooter />
  </Page>
);

// =========== Pillar IV, Pricing 2 ===========
const PagePricing2 = () => (
  <Page>
    <PageHeader pageNum="07" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar IV continued · Pricing Architecture II</SectionLabel>
      <H1>The math, the friction,<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>and the founding trap.</em></H1>

      <H2>Year 1 revenue projection, current vs recommended</H2>

      <P size="9.5pt" mb="3mm" color={COLORS.muted}>
        Assumptions held constant: 5 founding customers + 4 non-founding customers acquired in Year 1; 35% of customers self-select Plus tier; 20% prepay annually; multi-site events: 0 in Y1.
      </P>

      <Table
        cols={['Revenue line', 'Current model', 'Recommended', 'Δ Y1']}
        rows={[
          ['Subscription, 5 founding @ 40% off Y1', '€28,440', '€28,440', ','],
          ['Subscription, 4 non-founding @ full', '€37,920 (Foundation only)', '€42,888 (35% on Plus tier)', '+€4,968'],
          ['Implementation fees, 4 non-founding', '€0 (included)', '€19,800 (4 × €4,950)', '+€19,800'],
          ['Pilot fees, 5 trialled, 9 converted', '€22,500 (assume 9 trial)', '€22,500 (50% refundable, friction-adjusted: 13 trial)', '+€10,000 net'],
          ['User expansion, Cashel + 3 others +2 users avg', '€0 (unstated)', '€8,640 (8 users × €90 × 12)', '+€8,640'],
          ['Annual prepay discount cost (20% of base)', '–€2,800', '–€3,300', '–€500'],
          ['Total Y1 revenue', '€86,060', '€118,968 (Foundation-mix realistic) → €145,000+ at Plus-tier mix', '+€33,000–60,000'],
        ]}
        accent={COLORS.green}
      />

      <Callout tone="opp" title="Year 2 projection (compounding effect)">
        Year 2 ARR with the recommended model, assuming 70% retention, 6 new customers, founding-customers still at 30% discount but Y3 reverts to full price, projects to <strong style={{ color: COLORS.green }}>€220–260k</strong> vs <strong style={{ color: COLORS.red }}>€95–125k</strong> on the current model. The gap widens because implementation revenue stacks and Plus-tier conversions compound.
      </Callout>

      <H2>The Pilot Programme, friction analysis</H2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm', marginBottom: '3mm' }}>
        <div>
          <H3>What's blocking pilots</H3>
          <P size="8.5pt">
            €2,500 non-refundable is too high for a sceptical 12-person dairy. Industry benchmarks for paid pilots in this segment: €500–1,500, often refundable on conversion.
          </P>
          <P size="8.5pt">
            <strong style={{ color: COLORS.navy }}>Diagnostic:</strong> 10 prospects 70% interested. How many click "Start Pilot" at €2,500 non-refundable vs 50% refundable? Estimate: 3 vs 5. Same effort, 67% more pilots.
          </P>
        </div>
        <div>
          <H3>The recommended structure</H3>
          <P size="8.5pt" mb="1.5mm">
            <strong style={{ color: COLORS.navy }}>€2,500 Pilot, 50% refundable on walk-away</strong>
          </P>
          <ul style={{ margin: 0, paddingLeft: '4.5mm', fontSize: '8.5pt', color: COLORS.slate, lineHeight: 1.55 }}>
            <li>Convert: €2,500 credits towards implementation fee.</li>
            <li>Walk: €1,250 returned, €1,250 retained for white-glove time.</li>
            <li>You cover delivery cost. They have a real exit ramp.</li>
            <li>Frame as "skin in the game on both sides".</li>
          </ul>
        </div>
      </div>

      <H2>The founding-customer trap, recommended language</H2>
      <Callout tone="insight" title="Add to Founding Customer PDF + Pricing page">
        "<em>Founding customers receive 40% off in Year 1, 30% off in Year 2. From Year 3 onwards, they transition to standard pricing, but retain Founding Partner status, named recognition, and direct access to the build team for the lifetime of the account.</em>" Caps the commitment, preserves relationship; prevents customer #6 paying 40% more than #1 forever.
      </Callout>
    </div>
    <PageFooter />
  </Page>
);

// =========== Pillar V ===========
const PageCollateral = () => (
  <Page>
    <PageHeader pageNum="08" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Pillar V · Score 6 / 10, Restructure</SectionLabel>
      <H1>Sales Collateral & Demo<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>one brochure cannot do four jobs.</em></H1>

      <P>
        The current 9-page brochure is a single asset trying to serve cold-email, leave-behind, sales-meeting, and procurement-evaluation use-cases simultaneously. Each has different attention budgets and decision criteria. The fix is collateral segmentation.
      </P>

      <H2>Collateral matrix, what's needed</H2>
      <Table
        cols={['Asset', 'Use case', 'Length', 'Status']}
        rows={[
          ['9-page Brochure (existing)', 'Leave-behind after a discovery call. Prospect already engaged.', '9pp A4', '✅ Live'],
          ['2-page Executive Summary', 'Cold-email attachment. Decision-maker scan in 30 sec.', '2pp A4', '🔴 Build'],
          ['Cashel Blue Case Study', 'Sent on request. Sceptical buyer asking "show me one customer in detail".', '4pp A4', '🟡 Build (after stronger quotes land)'],
          ['Founding Customer PDF', 'Sent to qualified founding-partner prospects only.', '8pp A4', '✅ Live'],
          ['Compliance & Hosting brief', 'Sent when retailer compliance team or NIS2-aware buyer requests details.', '3pp A4', '🟡 Build'],
          ['ROI / Cost-of-inaction worksheet', 'Filled in during discovery call by founder, signed by both parties.', '1pp A4', '🟡 Build'],
          ['90-second product demo loop', 'Embedded on home page above fold. Auto-play, silent, looping.', 'Video', '🔴 Record'],
          ['Mobile/tablet screenshot reel', 'Features page. Cheese-floor reality check.', 'Image set', '🔴 Build'],
        ]}
      />

      <H2>Brochure-specific recommendations</H2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5mm' }}>
        <Callout tone="risk" title="Page 6 awards block">
          Verify each Cashel Blue award in writing before this brochure ever leaves your office. If even one award is wrong, you've put a customer's reputation at risk in your own marketing material, far worse than if you'd put it on your own logo strip.
        </Callout>
        <Callout tone="info" title="Page 4–5 feature listings">
          Currently text-heavy bullet feature lists. Consider replacing 50% of bullets with a single annotated screenshot per page. The feature list survives but the visual memory is much stronger.
        </Callout>
      </div>

      <H2>The 2-page Executive Summary brochure</H2>
      <P>
        For cold-email use, the existing 9-pager is too long. A 2-page summary that opens in 5 seconds and conveys the entire commercial proposition would dramatically increase reply rates. Recommended structure:
      </P>
      <Table
        cols={['Page', 'Content', 'Goal']}
        rows={[
          ['1', 'Hero proposition + Cashel Blue founding-partner badge + 3 outcome metrics + 1 trigger event hook + photo of the Cashel team', 'Convince in 30 seconds'],
          ['2', 'Pricing (€790/mo + €4,950 implementation) + Pilot Programme (€2,500 50% refundable) + Calendly QR + direct phone + email', 'One specific call-to-action with three response paths'],
        ]}
      />
    </div>
    <PageFooter />
  </Page>
);

// =========== Roadmap ===========
const PageRoadmap = () => (
  <Page>
    <PageHeader pageNum="09" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Action Roadmap</SectionLabel>
      <H1>48 hours · 30 days<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>· 90 days.</em></H1>

      <H2 color={COLORS.red}>🔴 Critical, next 48 hours (before any external launch)</H2>
      <Table
        cols={['Action', 'Owner', 'Outcome']}
        rows={[
          ['Verify Cashel Blue awards in writing. Remove or correct unverified claims on Brochure Page 6.', 'Founder', 'Removes legal exposure'],
          ['Singularise plural language across all assets, "founding partner" not "customers/producers".', 'Engineering', 'Removes credibility risk'],
          ['Send testimonial-request email to Louis, Sarah, Sergio (already drafted).', 'Founder', 'Stronger quotes in 7 days'],
          ['Add expiry to founding-customer discount: "Year 3 onward at standard pricing".', 'Engineering', 'Closes customer-#6 trap permanently'],
          ['Make Pilot 50% refundable. Update copy on Pricing & Founding Customer PDF.', 'Engineering', 'Removes ~30% of pilot friction'],
        ]}
        accent={COLORS.red}
      />

      <H2 color={COLORS.amber}>🟡 High value, next 30 days</H2>
      <Table
        cols={['Action', 'Owner', 'Outcome']}
        rows={[
          ['Record 90-second silent product demo loop. Embed on Home above fold.', 'Founder', '+18–25% home conversion'],
          ['Build 2-page Executive Summary brochure for cold email.', 'Engineering', '5× cold reply rate'],
          ['Add Plus tier (€1,290/mo) + Implementation fee to Pricing page.', 'Engineering', '+€4,950 / non-founding customer'],
          ['Reference-Call mechanism on contact form (internal alert when checked).', 'Engineering', 'Closes biggest sales objection'],
          ['Founder credibility block on Home, photo + affiliations badge strip.', 'Engineering', '+1.4× founder-led conversion'],
          ['Mobile/tablet screenshot reel + "anti-features" block.', 'Founder', '+credibility for sceptics'],
          ['Outreach to 2 additional founding-partner prospects.', 'Founder', 'Path to plural language'],
        ]}
        accent={COLORS.amber}
      />

      <H2 color={COLORS.green}>🟢 Strategic, next 90 days</H2>
      <Table
        cols={['Action', 'Owner', 'Outcome']}
        rows={[
          ['Long-form Cashel Blue case study (4pp PDF + landing page).', 'Founder', 'Becomes the asset every prospect requests'],
          ['Interactive product demo (Storylane / Arcade) + public read-only sandbox.', 'Engineering', '+15–20% home conversion'],
          ['ISO 27001 / Cyber Essentials certification process initiated.', 'Founder', 'Required for retailer-driven buyers'],
          ['Side-by-side proof reels (Excel vs Keystone × 3 workflows).', 'Founder', '+10–15% conversion'],
          ['Compliance & Hosting brief (3pp PDF) for retailer / NIS2 inquiries.', 'Engineering', 'Closes retailer-driven sales'],
          ['Convert 2nd & 3rd founding partner; document brand voice rules.', 'Founder', 'Trust score 5 → 8'],
        ]}
        accent={COLORS.green}
      />

    </div>
    <PageFooter />
  </Page>
);

// =========== Financial Impact ===========
const PageFinancial = () => (
  <Page>
    <PageHeader pageNum="10" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Financial Impact</SectionLabel>
      <H1>What seven days of work<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>actually pays back.</em></H1>

      <P>
        The recommendations in this report cluster into three commercial levers: <strong style={{ color: COLORS.navy }}>(a)</strong> raise revenue per customer (Plus tier, implementation fees, user expansion), <strong style={{ color: COLORS.navy }}>(b)</strong> reduce friction at the conversion gates (Pilot refundability, demo video, reference-call mechanism), and <strong style={{ color: COLORS.navy }}>(c)</strong> protect the brand against legal/credibility exposure (award verification, plural language, founding-customer trap).
      </P>

      <H2>Year 1 ARR, current model vs recommended</H2>
      <Table
        cols={['Scenario', 'Customers Y1', 'Subscription', 'Implementation', 'Pilot fees', 'Total Y1']}
        rows={[
          ['Current, Foundation only', '5 founding + 4 paid', '€66,360', '€0', '€22,500', '€88,860'],
          ['Recommended, Foundation only', '5 founding + 4 paid', '€71,328', '€19,800', '€22,500', '€113,628'],
          ['Recommended, 35% Plus mix', '5 founding + 4 paid (1 on Plus)', '€76,776', '€19,800', '€22,500', '€119,076'],
          ['Recommended, 35% Plus + reduced friction (13 pilots)', '5 founding + 6 paid (2 on Plus)', '€87,300', '€29,700', '€32,500', '€149,500'],
        ]}
      />

      <H2>Year 2 ARR, projection</H2>
      <Table
        cols={['Scenario', 'Retention', 'New customers Y2', 'Total Y2 ARR']}
        rows={[
          ['Current, same model', '70%', '6', '€95–125k'],
          ['Recommended, same model', '80% (better onboarding)', '8 (better collateral)', '€185–225k'],
          ['Recommended, Plus stable', '80%', '10', '€220–280k'],
        ]}
        accent={COLORS.green}
      />

      <Callout tone="opp" title="Compounding effect">
        The biggest reason the Y2 number widens isn't more customers, it's the <strong style={{ color: COLORS.green }}>implementation revenue stack</strong>. Every new customer adds €4,950 in non-recurring revenue that doesn't exist in the current model. By Y2, with 12–14 paying customers, that's <strong style={{ color: COLORS.green }}>€60–70k of additional cashflow per year</strong> from a single architectural change to the pricing page.
      </Callout>

      <H2>What does NOT change</H2>
      <P>
        Subscription pricing stays at €790/mo for Foundation. Calendly link, contact email, brand identity, design system, copy voice, hosting setup, technical product, founding-customer narrative, Cashel Blue relationship, none of these are touched. Every recommendation in this report can be implemented without a single change to the underlying product or the existing customer experience for Cashel Blue.
      </P>

      <H2>Cost of doing nothing</H2>
      <Callout tone="risk" title="The shadow Year 1 number">
        If the recommendations are not implemented, and the current site/brochure/pricing model goes to market unchanged, the realistic Year 1 ARR is the <strong style={{ color: COLORS.red }}>€88,860 column</strong>. The €30–60k gap between that and the recommended model is recurring, it doesn't disappear in Year 2; it widens. <strong style={{ color: COLORS.red }}>Cost of doing nothing across Years 1+2: approximately €120–180k.</strong>
      </Callout>
    </div>
    <PageFooter />
  </Page>
);

// =========== Risk Register ===========
const PageRisks = () => (
  <Page>
    <PageHeader pageNum="11" />
    <div style={{ marginTop: '5mm', flex: 1 }}>
      <SectionLabel>Risk Register</SectionLabel>
      <H1>Things that can go wrong<br/><em style={{ fontStyle: 'italic', color: COLORS.magenta, fontWeight: 700 }}>before they go right.</em></H1>

      <H2>Legal & reputational</H2>
      <Table
        cols={['Risk', 'Likelihood', 'Impact', 'Mitigation']}
        rows={[
          ['Cashel Blue award claims unverifiable → IP / misrepresentation exposure', 'Medium', 'High', 'Verify each award in writing inside 48hr; remove if unverifiable'],
          ['Plural-customer language perceived as misleading by sceptical buyer', 'High', 'Medium', 'Singularise immediately; "founding partner" until n≥3'],
          ['Compliance copy ("ISO 27001", "NIS2 ready") used before certification complete', 'High at scale', 'High', '"ISO 27001 alignment in progress" / "NIS2 designed-for posture", never "certified"'],
          ['Customer photo / logo used beyond agreed scope', 'Low', 'Medium', 'Get scope of use signed by Cashel Blue (logo, photo, name)'],
        ]}
      />

      <H2>Commercial</H2>
      <Table
        cols={['Risk', 'Likelihood', 'Impact', 'Mitigation']}
        rows={[
          ['Founding-customer discount grandfathered forever (customer-#6 trap)', 'Certainty unless changed', 'High recurring', 'Cap at Y2; revert to standard Y3'],
          ['€2,500 non-refundable Pilot blocks 30%+ of qualified pilots', 'High', 'Medium', '50% refundable variant'],
          ['No Plus tier → account value capped at Foundation forever', 'Certainty', 'Medium recurring', 'Add Plus tier'],
          ['Cashel Blue churn before 2nd customer lands → narrative collapses', 'Low', 'Catastrophic', 'Land 2nd customer urgently'],
          ['No multi-site pricing → renegotiation in worst position when Cashel #2 happens', 'Medium', 'Medium', 'Publish multi-site policy now (+50% per site)'],
        ]}
      />

      <H2>Operational</H2>
      <Table
        cols={['Risk', 'Likelihood', 'Impact', 'Mitigation']}
        rows={[
          ['Brand voice drift as team grows', 'High over 12mo', 'High recurring', 'Document voice rules; protect via review checklist'],
          ['Founder bandwidth bottleneck on white-glove implementations', 'Certainty at >5 customers', 'High', 'Productise implementation; train delivery person before 6th customer'],
          ['Self-managed AWS hosting failure with no failover', 'Low / tail-heavy', 'Catastrophic', 'Document recovery; consider managed Ireland tenancy commitment'],
        ]}
      />

      <Callout tone="insight" title="The single biggest risk on this entire list">
        It is <em>not</em> any item above. It is the risk of <strong style={{ color: COLORS.navy }}>not landing a 2nd founding partner inside the next 8–12 weeks</strong>. Every other risk has a 24-hour fix. This one requires sales motion. Allocate founder time to it before any of the engineering work in this report.
      </Callout>
    </div>
    <PageFooter />
  </Page>
);

// =========== Conclusion ===========
const PageConclusion = () => (
  <Page bg={COLORS.navy}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <KeystoneLogo height={36} variant="dark" showTagline={false} />
      <div style={{
        fontSize: '8pt', letterSpacing: '0.22em', textTransform: 'uppercase',
        fontWeight: 700, color: COLORS.gold,
      }}>P. 12 / 12, Conclusion</div>
    </div>

    <div style={{ marginTop: '12mm', flex: 1, color: '#fff' }}>
      <SectionLabel color={COLORS.gold}>Conclusion · Next 7 Days</SectionLabel>
      <H1 color="#fff">
        Seven days of focused work<br/>
        moves every score,<br/>
        <em style={{ fontStyle: 'italic', color: COLORS.gold, fontWeight: 700 }}>and Year 1 ARR.</em>
      </H1>

      <p style={{ fontSize: '10pt', lineHeight: 1.55, color: '#CBD5E0', marginTop: '5mm', maxWidth: '155mm' }}>
        This report identifies a 5/10 trust score, a 4/10 solution-proof score, and a 6/10 pricing model, all addressable inside 30 days with no change to the underlying product. Composite score moves from 6 to 8, financial impact lands €30–60k of incremental Year 1 revenue, and the assets become legally defensible before any external launch.
      </p>

      <h2 style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: '14pt', color: COLORS.gold, marginTop: '8mm', marginBottom: '4mm',
        fontWeight: 700, letterSpacing: '-0.01em',
      }}>The seven-day action list, in priority order</h2>

      <ol style={{ margin: 0, paddingLeft: '5mm', fontSize: '9.5pt', color: '#fff', lineHeight: 1.6 }}>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 1.</strong> Email Sarah, Sergio, Louis: testimonial request + award verification + scope-of-use sign-off. One email, three asks.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 1.</strong> Singularise plural language across the site (~15 min of engineering).</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 2.</strong> Add expiry to founding-customer discount on Pricing + Founding Customer PDF.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 2.</strong> Convert Pilot to 50% refundable.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 3.</strong> Add Plus tier + Implementation fee on Pricing page.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 3.</strong> Founder credibility block on Home (photo + affiliations).</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 4.</strong> Record 90-second silent demo loop. Embed on Home.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 5.</strong> Reference-call checkbox on contact form + internal alert.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 6.</strong> 2-page Executive Summary brochure for cold email.</li>
        <li style={{ marginBottom: '2mm' }}><strong style={{ color: COLORS.gold }}>Day 7.</strong> Identify 2 additional founding-partner prospects. Begin outreach.</li>
      </ol>

      <div style={{
        marginTop: '8mm', padding: '5mm 7mm',
        background: 'rgba(201, 169, 97, 0.08)',
        border: `1px solid ${COLORS.gold}40`,
        borderLeft: `3px solid ${COLORS.gold}`,
        borderRadius: 4,
      }}>
        <div style={{
          fontSize: '7.5pt', letterSpacing: '0.22em', textTransform: 'uppercase',
          fontWeight: 700, color: COLORS.gold, marginBottom: '2mm',
        }}>The honest summary</div>
        <p style={{ fontSize: '10.5pt', lineHeight: 1.45, color: '#fff', margin: 0, fontStyle: 'italic' }}>
          The brand work is genuinely top-decile and worth defending fiercely. The commercial machine attached to it needs seven days of structural work to match. After that, this becomes a serious B2B SaaS proposition for the Irish artisan food segment, not a beautifully designed brochure-ware site with a fragile revenue model behind it.
        </p>
      </div>
    </div>

    <div style={{
      borderTop: `1px solid rgba(255,255,255,0.15)`, paddingTop: '3mm', marginTop: '5mm',
      display: 'flex', justifyContent: 'space-between', fontSize: '7.5pt', color: '#94A3B8',
    }}>
      <div>Keystone by CloudVoro · Strategic Memo · February 2026</div>
      <div>Confidential · For Asif Khan only</div>
    </div>
  </Page>
);

// ============== Toolbar ==============

const Toolbar = () => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadServerPDF('/strategy', 'Keystone-Strategic-Recommendations.pdf');
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('PDF export failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="no-print" style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: '#fff', borderBottom: `1px solid ${COLORS.border}`,
      padding: '12px 24px', display: 'flex',
      justifyContent: 'space-between', alignItems: 'center',
      boxShadow: '0 2px 8px rgba(10,37,64,0.06)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <a
          href="/"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px',
            background: COLORS.bgSoft,
            color: COLORS.navy,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 600,
            textDecoration: 'none',
            letterSpacing: '0.01em',
          }}
          data-testid="strategy-back-to-site"
        >
          ← Back to site
        </a>
        <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
          <KeystoneLogo height={36} variant="light" showTagline={false} />
          <div style={{ borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Strategic Recommendations</div>
            <div style={{ fontSize: 11, color: COLORS.muted }}>12 pages · A4 · Confidential · Internal</div>
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => window.print()}
          data-testid="strategy-print-btn"
          style={{
            background: '#fff', color: COLORS.navy,
            border: `1px solid ${COLORS.border}`,
            padding: '9px 16px', borderRadius: 6,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          🖨  Print
        </button>
        <button
          onClick={handleDownload}
          disabled={downloading}
          data-testid="strategy-download-pdf-btn"
          style={{
            background: downloading ? COLORS.muted : COLORS.navy,
            color: '#fff', border: 'none',
            padding: '10px 22px', borderRadius: 6,
            fontSize: 13, fontWeight: 700,
            cursor: downloading ? 'wait' : 'pointer',
            letterSpacing: '0.02em',
            boxShadow: '0 4px 12px rgba(10,37,64,0.25)',
          }}
        >
          {downloading ? '⏳  Generating PDF…' : '⬇  Download PDF'}
        </button>
      </div>
    </div>
  );
};

// ============== Page ==============

export default function StrategyReport() {
  return (
    <div style={{
      minHeight: '100vh', background: '#E8EAEC',
      padding: '0 0 60px',
    }}>
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .brochure-stack { padding: 0 !important; }
          .brochure-page {
            box-shadow: none !important;
            margin: 0 !important;
            box-sizing: border-box !important;
            page-break-after: always;
            break-after: page;
            border: none !important;
          }
          .brochure-page:last-of-type {
            page-break-after: auto !important;
            break-after: auto !important;
          }
        }
      `}</style>
      <Toolbar />
      <div className="brochure-stack" style={{ padding: '24px 0' }}>
        <PageCover />
        <PageScorecard />
        <PageBrandCopy />
        <PageTrust />
        <PageProof />
        <PagePricing1 />
        <PagePricing2 />
        <PageCollateral />
        <PageRoadmap />
        <PageFinancial />
        <PageRisks />
        <PageConclusion />
      </div>
    </div>
  );
}
